import type { IMaintenanceStatus } from '@featit/shared';

/**
 * How often an idle tab asks whether a deploy window has opened.
 *
 * This is the one request a reader makes whether or not they do anything, so its cost is
 * `open tabs × 3600/interval` and nothing else — at two seconds a thousand idle tabs are 500
 * requests a second to be told "no" every time (I-0232).
 *
 * It can afford to be slow because it is not how the notice usually arrives. `ApiService`'s
 * response interceptor turns any 503 carrying a maintenance status into a published status, so
 * anything the app does for its own reasons announces the window immediately. What is left for this
 * poll is a tab so idle nothing else is talking — a screen nobody is looking at, where being a
 * minute late costs nothing.
 *
 * Deployments set it by environment: 60s where releases are frequent and a tester wants to see the
 * banner promptly, 300s in production where they are planned and rare and the reader count is the
 * number that matters. A window that opens and closes inside one interval may never reach a fully
 * idle tab at all, which is correct — nothing of that reader's was interrupted, and the first thing
 * they do brings the interceptor with it.
 */
export const DEFAULT_MAINTENANCE_POLL_INTERVAL_MS = 60_000;

/**
 * How often a tab that has lost the server asks whether it is back.
 *
 * Used only while the notice is on screen and the server gave us no cadence of its own — an
 * unparseable 503, or a request that never arrived. The reader is watching a banner that promises
 * "Retrying automatically", so this is the one state where a short interval is the honest one.
 */
export const MAINTENANCE_RECOVERY_POLL_INTERVAL_MS = 5_000;

export type MaintenanceNoticeState =
  | { kind: 'maintenance'; status: IMaintenanceStatus }
  | { kind: 'offline' }
  | null;

export function readMaintenancePollIntervalMs(
  raw: unknown,
  fallback = DEFAULT_MAINTENANCE_POLL_INTERVAL_MS,
): number {
  const parsed = typeof raw === 'string' ? Number(raw.trim()) : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * The delay before the next check, given what the last one found.
 *
 * Three states, three different questions, and only one of them is ours to answer. While a window is
 * open the server has already said how often it wants to be asked — `retryAfterSeconds`, written by
 * `stable-maintenance.sh activate` and validated on the way in — so we use its number rather than
 * one of our own. Guessing here would mean either hammering a deployment that asked for room, or
 * leaving the banner up after the release has landed.
 */
export function maintenancePollDelayMs(
  notice: MaintenanceNoticeState,
  healthyIntervalMs: number,
): number {
  if (notice === null) return healthyIntervalMs;
  if (notice.kind === 'offline') return MAINTENANCE_RECOVERY_POLL_INTERVAL_MS;
  return notice.status.retryAfterSeconds * 1000;
}
